/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import picturefill from 'picturefill';
import objectFitImages from 'object-fit-images';
import Icon from 'components/Icons/Maximize/Maximize';
import Picture from 'components/Picture/Picture';
import Spinner from 'components/Spinner/Spinner';
import './Slide.scss';

/**
 * This is the Slide component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Slide({ focalPoint, image, maximize, dynamicHeight, onClick }) {
  const node = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [lodaded, setLoaded] = useState(false);
  const ratio = image.img.height / image.img.width;

  useEffect(() => {
    picturefill({
      elements: [node.current],
    });
    objectFitImages(node.current);
    setMounted(true);
  }, []);

  function getStyle() {
    const style = {
      '--focal-point-x': focalPoint.x * 100 + '%',
      '--focal-point-y': focalPoint.y * 100 + '%',
    };

    if (dynamicHeight) {
      return {
        ...style,
        aspectRatio: `${image.img.width}/${image.img.height}`,
      };
    }
    return style;
  }

  return (
    <div className={classnames('slide', { portrait: ratio > 1, mounted })}>
      <div style={getStyle()}>
        <Picture
          draggable="false"
          {...image}
          onLoad={() => {
            setLoaded(true);
          }}
          onError={() => {
            setLoaded(true);
          }}
        />
        {lodaded ? null : <Spinner />}
        <button onClick={onClick}>
          <Icon title={maximize} />
        </button>
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Slide.propTypes = {
  focalPoint: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }).isRequired,
  image: PropTypes.shape({
    img: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
    }).isRequired,
  }).isRequired,
  maximize: PropTypes.string.isRequired,
  dynamicHeight: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Slide.defaultProps = {
  focalPoint: {
    x: 0.5,
    y: 0.5,
  },
  image: {
    img: {},
  },
  maximize: 'Maximize',
  dynamicHeight: false,
  onClick: () => {},
};

export default Slide;
